/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import hashHistory from '@src/History';
import { withRouter } from 'react-router';
import Provider from 'relements/build/esm/theme/provider';
import Topbar from '@haptik/helpers/build/esm/Topbar';
import {
  setCorsEnableFlag,
  setRephraseResponsesFlag,
  setFeedbackMessagesFlag,
  entityV2EnabledCheck,
  verifyDisableLanguageGuardrails,
} from '@src/actions/_utils';
import EmptyState from '@haptik/helpers/build/esm/EmptyState';
import InAppBanner from '@haptik/helpers/build/esm/InAppBanner';
import { BASE_URL } from '@src/const';
import { isProduction, handleEmptyIVAList } from '@src/utils';
import { fetchTestConfiguration } from '@src/actions/DomainActions';
import Header from 'containers/Header';
import Utils from 'containers/Utils';
import VerticalMenu from '@src/components/VerticalMenu';
import Sidebar from '@src/components/Sidebar';
import { getAllStories } from 'actions/StoryActions';
import { logout, createNewStory, openComponentModal } from 'actions/BotActions';
import CreateTemplateModal from 'containers/Templates/CreateTemplateModal';

import CommonStore from '@src/stores/CommonStore';

import Locked from '@src/components/Icon/locked.svg';

import { FEATURE_FLAG_DATA } from '@src/constants/MogamboConstants';

import BotStore from '../store';

import './App.scss';
import { StyledCenterText } from './SubStories/Graph/Graph';

@withRouter
export default class App extends React.Component {
  constructor() {
    super();
    this.state = {
      currDomain: '',
      ivaSwitched: false,
      isEmptyIVAList: false,
      initialIVAFetch: true,
    };
    this.topbarRef = React.createRef();
    this.appLoaded = false;
  }

  componentDidMount() {
    if (this.topbarRef) {
      BotStore.setTopbarEvents(this.topbarRef.current);
      BotStore.emitChange();
    }
    const isVoicePage = window.location.hash.includes('in-browser-calling');
    if (isVoicePage) {
      const script = document.createElement('script');
      script.src =
        'https://media.twiliocdn.com/sdk/js/client/v1.13/twilio.min.js';
      script.async = true;
      script.onload = () => {
        console.log('✅ Twilio SDK Loaded!');
        console.log(window.Twilio); // Verify if Twilio is available
      };
      script.setAttribute('crossorigin', 'anonymous');
      document.body.appendChild(script);
    }
  }

  componentDidUpdate() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    const ivaString = localStorage.getItem('ww_iva');
    const selectedIVA = JSON.parse(ivaString ?? '{}');
    if (hasSelfServeLayout) {
      if (
        window.location.hash === '#/bots' ||
        window.location.hash === '#/businesses'
      ) {
        this.routeToSelectedIVA();
      }
      if (selectedIVA?.domain_type === 2 && window.location.hash === '#/') {
        this.handleIVASelect(selectedIVA);
      }
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const currBot = CommonStore.getMetaTags()?.domain?.id;
    const currBotName = CommonStore.getMetaTags()?.domain?.title;
    const testConfig = BotStore.getTestConfigData();
    if (currBot && currBot !== prevState.currDomain) {
      if (Object.keys(testConfig).length === 0) {
        fetchTestConfiguration(currBot);
      }
      return { currDomain: currBot, ivaSwitched: true };
    }
    return null;
  }

  routeToSelectedIVA = () => {
    const selectedIVA = JSON.parse(localStorage.getItem('ww_iva'));
    if (selectedIVA?.id) {
      this.reroute(selectedIVA);
    }
  };

  resetIVASwitched = () => {
    setTimeout(() => {
      this.setState({
        ivaSwitched: false,
      });
    }, 1500);
  };

  reroute = (iva) => {
    const domainId = iva.domain_id || iva.id;
    getAllStories(domainId, () => {
      BotStore.setIsSmartSkillSelectedViaPage(false);
      BotStore.emitChange();
      const allStories = BotStore.getAllStories().filter((item) => item.active);
      if (allStories.length > 0) {
        if (iva.isSmartSkill) {
          this.resetIVASwitched();
        }
        // not routing when editing zoho // need to stop on initial mounting
        if (!window.location.hash.includes('message_delivery_zoho'))
          hashHistory.push(
            `/v2/bots/${domainId}/stories/${
              this.getStoryToNavigate(allStories).id
            }/substories`
          );
      } else {
        // If the selected bot has no stories. Create one and navigate to that story.
        // This will only happen for old bots that doesn't have a substory. All the new bots will have a story by default.
        this.createSkillset(iva);
      }
    });
  };

  getStoryToNavigate = (allStories) => {
    const defaultStory = allStories[0].title.includes('Feedback')
      ? allStories.length > 1
        ? allStories[1]
        : allStories[0]
      : allStories[0];
    const selectedStory = localStorage.getItem('selected_skillset');
    const storyToNavigate = allStories.find(
      (story) => story.id === selectedStory
    );
    return storyToNavigate || defaultStory;
  };

  createSkillset = (iva) => {
    const domainId = iva.domain_id || iva.id;
    const domainName = iva.name || iva.title;

    createNewStory('Skillset-1', domainId, domainName, (response) => {
      localStorage.setItem('selected_skillset', response.story.id);
      hashHistory.push(
        `/v2/bots/${iva.id}/stories/${response.story.id}/substories`
      );
    });
  };

  handleIVASelect = (iva) => {
    const domainId = iva.domain_id || iva.id;
    const editMode = CommonStore.getEditMode();
    const businesses =
      iva.business || [
        { id: iva?.business_id, via_name: iva?.business_via_name },
      ] ||
      [];
    const isVoicePage = window.location.hash.includes('in-browser-calling');
    if (!this.state.ivaSwitched && !isVoicePage) {
      if (!editMode) {
        this.reroute(iva);
        fetchTestConfiguration(domainId);
      } else if (editMode) {
        hashHistory.push();
      }
      this.resetIVASwitched();
    } else {
      this.resetIVASwitched();
    }

    if (this.appLoaded) {
      localStorage.removeItem('selected_skillset');
    } else {
      this.appLoaded = true;
    }

    setRephraseResponsesFlag(iva.title || iva.domain_name);
    setFeedbackMessagesFlag(
      iva.business?.[0]?.via_name || iva.business_via_name
    );
    setCorsEnableFlag(iva.client);
    entityV2EnabledCheck(iva.title || iva.domain_name);
    verifyDisableLanguageGuardrails(iva.title || iva.domain_name);
    BotStore.setBusinessesLinkedToBot(businesses);
    BotStore.emitChange();
  };

  handleSmartSkillCreate = () => {
    openComponentModal(
      <CreateTemplateModal />,
      'Create a New Smart Skill',
      null,
      'close'
    );
  };

  onIVAFetch = (IVAList) => {
    // Handling the case of empty IVA list.
    if (this.state.initialIVAFetch) {
      if (IVAList.length === 0) {
        this.setState({
          isEmptyIVAList: true,
        });

        // User will be redirected to create bot page when the env is not production
        if (!isProduction()) {
          handleEmptyIVAList();
        }
      } else {
        this.setState({
          isEmptyIVAList: false,
        });
      }
      this.setState({
        initialIVAFetch: false,
      });
    }
  };

  getIsProMDisabled = (env) => {
    if (
      env === 'production' ||
      // on prestaging value of env is staging only
      window.location.origin === 'https://prestaging.hellohaptik.com'
    ) {
      return false;
    }
    return true;
  };

  renderApp = () => {
    const selectedIVA = JSON.parse(localStorage.getItem('ww_iva'));
    const userData = BotStore.getUserData();
    const env = BotStore.getEnvironment();
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    const featureTogglesForProM = BotStore.getFeaturesDataForProM();
    const baseURL = `${BASE_URL.replace('/mogambo_api/', '')}`;
    const isSmartSkillSelectedViaPage = BotStore.getIsSmartSkillSelectedViaPage();
    const selectedBot = !isSmartSkillSelectedViaPage
      ? selectedIVA?.domain_id ||
        selectedIVA?.id ||
        this.state.currDomain ||
        CommonStore.getMetaTags()?.domain?.id
      : this.state.currDomain || CommonStore.getMetaTags()?.domain?.id;
    const mixPanelTracker = BotStore.getMixPanel();
    // eslint-disable-next-line no-mixed-operators
    const showUi =
      BotStore.getTestConfigData().active ||
      (!('active' in BotStore.getTestConfigData()) &&
        'base_url' in BotStore.getTestConfigData());

    const isVoicePage = window.location.hash.includes('in-browser-calling');

    return (
      <div className="app">
        {userData.permissions.length > 0 && hasSelfServeLayout && !isVoicePage && (
          <>
            <Topbar
              baseURL={baseURL}
              userDetails={{
                ...userData,
              }}
              env={env === 'production' || env === 'staging' ? env : 'staging'}
              botID={selectedBot}
              screen="CS"
              onIVAFetch={this.onIVAFetch}
              onIVAChange={this.handleIVASelect}
              onSignOut={() => logout()}
              ref={this.topbarRef}
              onCreateSmartSkillClick={this.handleSmartSkillCreate}
              featureToggles={featureTogglesForProM}
              isProMDisabled={this.getIsProMDisabled(env)}
              mixPanelTracker={mixPanelTracker}
            />
            <InAppBanner
              casName={BotStore.getCAS()}
              environment={env}
              platform="CS"
              style={{
                width: 'calc(100% - 64px)',
                left: '64px',
                top: '64px',
                zIndex: '100',
              }}
            />
          </>
        )}
        {env === 'production' && this.state.isEmptyIVAList ? (
          <EmptyState
            title="Production Environment is locked."
            icon={Locked}
            subtitle="Build &amp; publish your first bot in order to access the production environment."
          />
        ) : (
          <div
            className={`app-default ${
              hasSelfServeLayout && !isVoicePage ? 'app-default-ww' : ''
            }`}
          >
            {showUi ? (
              <>
                {!isVoicePage &&
                  (hasSelfServeLayout ? <Sidebar /> : <VerticalMenu />)}
                <div id="app-body" className="app-body">
                  {!hasSelfServeLayout && !isVoicePage && (
                    <Header {...this.props} />
                  )}
                  {this.props.children}
                </div>
              </>
            ) : (
              isVoicePage && (
                <div id="app-body" className="app-body">
                  {this.props.children}
                </div>
              )
            )}

            {BotStore.getTestConfigData().active === false && (
              <div id="app-body" className="app-body">
                <StyledCenterText>
                  This bot has been decommissioned. Please reach out to the
                  Support team for re-enabling it
                </StyledCenterText>
              </div>
            )}
            <Utils {...this.props} />
          </div>
        )}
      </div>
    );
  };

  render() {
    return (
      <Provider
        theme={{
          primaryColor: '#0C4DA2',
        }}
      >
        {this.renderApp()}
      </Provider>
    );
  }
}
